import { useState, useEffect } from 'react';
import { api, getCurrentMonth, formatCurrency } from '../../lib/api';
import { Wallet, Calendar, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, AlertTriangle, TrendingUp, Receipt, Activity } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip, ReferenceLine } from 'recharts';

const MOIS_NOMS = ['', 'Janvier', 'Fevrier', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Aout', 'Septembre', 'Octobre', 'Novembre', 'Decembre'];

const Tresorerie = () => {
  const [mois, setMois] = useState(getCurrentMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/tresorerie/${mois}`);
      setData(res.data);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, [mois]);

  const navigateMonth = (dir) => {
    const [y, m] = mois.split('-').map(Number);
    let nm = m + dir, ny = y;
    if (nm > 12) { nm = 1; ny++; }
    if (nm < 1) { nm = 12; ny--; }
    setMois(`${ny}-${String(nm).padStart(2, '0')}`);
  };

  const [annee, moisNum] = mois.split('-').map(Number);
  const moisNom = MOIS_NOMS[moisNum];

  if (loading) return (
    <div className="flex flex-col items-center justify-center py-32 gap-4">
      <Activity size={28} className="animate-pulse-slow" style={{ color: 'rgb(var(--lm-gold))' }} />
      <span className="text-xs tracking-[0.3em] uppercase text-[rgb(var(--lm-text-muted))]">Chargement</span>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="tresorerie-page">
      {/* Header */}
      <div className="flex items-end justify-between animate-slide-up">
        <div>
          <p className="text-[10px] tracking-[0.2em] uppercase text-[rgb(var(--lm-text-muted))] mb-1">Tresorerie</p>
          <h1 className="font-title text-2xl text-[rgb(var(--lm-text))] flex items-center gap-2">
            <Wallet size={20} style={{ color: 'rgb(var(--lm-gold))' }} />
            {moisNom} {annee}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => navigateMonth(-1)} className="text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-gold))]"><ChevronLeft size={16} /></Button>
          <Button variant="ghost" size="sm" onClick={() => navigateMonth(1)} className="text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-gold))]"><ChevronRight size={16} /></Button>
        </div>
      </div>

      {data && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-3 gap-3">
            <div className="lm-card p-5 animate-slide-up" style={{ animationDelay: '60ms', animationFillMode: 'backwards' }} data-testid="kpi-entrees">
              <span className="text-[10px] tracking-[0.15em] uppercase text-[rgb(var(--lm-text-muted))]">Entrees (CA)</span>
              <p className="font-mono text-xl mt-1" style={{ color: 'rgb(var(--lm-green))' }}>{formatCurrency(data.total_ca_mois)}</p>
            </div>
            <div className="lm-card p-5 animate-slide-up" style={{ animationDelay: '120ms', animationFillMode: 'backwards' }} data-testid="kpi-sorties">
              <span className="text-[10px] tracking-[0.15em] uppercase text-[rgb(var(--lm-text-muted))]">Sorties fixes</span>
              <p className="font-mono text-xl mt-1" style={{ color: 'rgb(var(--lm-red))' }}>{formatCurrency(data.total_sorties)}</p>
            </div>
            <div className="lm-card p-5 animate-slide-up" style={{ animationDelay: '180ms', animationFillMode: 'backwards' }} data-testid="kpi-solde">
              <span className="text-[10px] tracking-[0.15em] uppercase text-[rgb(var(--lm-text-muted))]">Solde estime</span>
              <p className={`font-mono text-xl mt-1 ${data.solde_estime >= 0 ? 'text-[rgb(var(--lm-green))]' : 'text-[rgb(var(--lm-red))]'}`}>
                {formatCurrency(data.solde_estime)}
              </p>
            </div>
          </div>

          {/* TVA position */}
          <div className="lm-card p-5 animate-slide-up" style={{ animationDelay: '240ms', animationFillMode: 'backwards' }} data-testid="tva-position">
            <div className="flex items-center gap-2 mb-4">
              <Receipt size={14} style={{ color: 'rgb(var(--lm-red))' }} />
              <span className="text-xs tracking-[0.15em] uppercase text-[rgb(var(--lm-text-secondary))]">Position TVA du mois</span>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="text-center bg-[rgb(var(--lm-surface-elevated))] rounded-sm p-3">
                <p className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider">Collectee</p>
                <p className="font-mono text-sm text-[rgb(var(--lm-text))] mt-1">{formatCurrency(data.tva?.collectee || 0)}</p>
              </div>
              <div className="text-center bg-[rgb(var(--lm-surface-elevated))] rounded-sm p-3">
                <p className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider">Deductible</p>
                <p className="font-mono text-sm mt-1" style={{ color: 'rgb(var(--lm-green))' }}>-{formatCurrency(data.tva?.deductible || 0)}</p>
              </div>
              <div className={`text-center rounded-sm p-3 ${(data.tva?.nette || 0) > 0 ? 'bg-[rgb(var(--lm-red)/0.06)]' : 'bg-[rgb(var(--lm-green)/0.06)]'}`}>
                <p className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider">A payer</p>
                <p className={`font-mono text-sm font-bold mt-1 ${(data.tva?.nette || 0) > 0 ? 'text-[rgb(var(--lm-red))]' : 'text-[rgb(var(--lm-green))]'}`}>
                  {formatCurrency(data.tva?.nette || 0)}
                </p>
              </div>
            </div>
          </div>

          {/* Cash flow chart */}
          {data.tresorerie_jour?.length > 0 && (
            <div className="lm-card p-5 animate-slide-up" style={{ animationDelay: '300ms', animationFillMode: 'backwards' }} data-testid="cashflow-chart">
              <div className="flex items-center gap-2 mb-5">
                <TrendingUp size={14} style={{ color: 'rgb(var(--lm-gold))' }} />
                <span className="text-xs tracking-[0.15em] uppercase text-[rgb(var(--lm-text-secondary))]">Flux de tresorerie journalier</span>
              </div>
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={data.tresorerie_jour}>
                  <defs>
                    <linearGradient id="soldeGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="rgb(var(--lm-gold))" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="rgb(var(--lm-gold))" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="jour" tick={{ fill: 'rgb(var(--lm-text-muted))', fontSize: 10, fontFamily: 'Manrope' }} interval={4} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: 'rgb(var(--lm-text-muted))', fontSize: 10, fontFamily: 'JetBrains Mono' }} tickFormatter={v => `${(v/1000).toFixed(0)}k`} axisLine={false} tickLine={false} width={32} />
                  <Tooltip
                    contentStyle={{ background: 'rgb(var(--lm-surface))', border: '1px solid rgb(var(--lm-border))', borderRadius: 2, fontSize: 12, fontFamily: 'Manrope' }}
                    labelFormatter={v => `Jour ${v}`}
                    formatter={(v, name) => [formatCurrency(v), name === 'solde' ? 'Solde cumule' : name]}
                  />
                  <ReferenceLine y={0} stroke="rgb(var(--lm-text-muted))" strokeDasharray="3 3" />
                  <Area type="monotone" dataKey="solde" stroke="rgb(var(--lm-gold))" fill="url(#soldeGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Echeancier */}
          <div className="lm-card p-5 animate-slide-up" style={{ animationDelay: '360ms', animationFillMode: 'backwards' }} data-testid="echeancier">
            <div className="flex items-center gap-2 mb-5">
              <Calendar size={14} style={{ color: 'rgb(var(--lm-gold))' }} />
              <span className="text-xs tracking-[0.15em] uppercase text-[rgb(var(--lm-text-secondary))]">Echeancier des sorties</span>
            </div>
            <div className="space-y-1.5">
              {data.echeancier?.map((e, i) => (
                <div key={i} className="rounded-sm bg-[rgb(var(--lm-surface-elevated))] overflow-hidden">
                  <div
                    className="flex items-center justify-between p-3 cursor-pointer hover:bg-[rgb(var(--lm-gold)/0.03)] transition-colors"
                    onClick={() => e.detail && setExpanded(expanded === i ? null : i)}
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <span className="font-mono text-xs text-[rgb(var(--lm-text-muted))] w-6 text-right shrink-0">{e.jour}</span>
                      <span className="text-[10px] font-medium px-2 py-0.5 rounded-sm bg-[rgb(var(--lm-gold)/0.08)] text-[rgb(var(--lm-gold))] shrink-0">
                        {e.categorie}
                      </span>
                      <span className="text-sm text-[rgb(var(--lm-text))] truncate">{e.libelle}</span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0 ml-2">
                      <span className="font-mono text-[rgb(var(--lm-red))]">{formatCurrency(e.montant)}</span>
                      {e.detail && (expanded === i ? <ChevronUp size={12} className="text-[rgb(var(--lm-text-muted))]" /> : <ChevronDown size={12} className="text-[rgb(var(--lm-text-muted))]" />)}
                    </div>
                  </div>
                  {expanded === i && e.detail && (
                    <div className="px-3 pb-3 space-y-1 animate-slide-up">
                      {e.detail.map((d, j) => (
                        <div key={j} className="flex justify-between text-xs bg-[rgb(var(--lm-bg))] rounded-sm p-2">
                          <span className="text-[rgb(var(--lm-text-secondary))]">{d.libelle}</span>
                          <span className="font-mono text-[rgb(var(--lm-text))]">{formatCurrency(d.montant)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-between items-center mt-5 pt-4 border-t border-[rgb(var(--lm-border))]">
              <span className="text-xs tracking-[0.15em] uppercase text-[rgb(var(--lm-text-secondary))]">Total sorties fixes</span>
              <span className="font-mono text-xl text-[rgb(var(--lm-red))]">{formatCurrency(data.total_sorties)}</span>
            </div>
          </div>

          {/* Alert if deficit */}
          {data.solde_estime < 0 && (
            <div className="alert-card alert-danger flex items-center gap-3 animate-fade-in" data-testid="deficit-alert">
              <AlertTriangle size={16} />
              <span className="text-sm">Attention : deficit estime de {formatCurrency(Math.abs(data.solde_estime))} ce mois</span>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Tresorerie;
